import React, { useState, useEffect } from 'react';
import GameService from '../services/GameService';
import {
  FiPlay,
  FiPower,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiEdit,
  FiAward,
  FiX,
  FiCheck,
  FiInfo,
} from 'react-icons/fi';

const Games = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [filter, setFilter] = useState('ALL'); // ALL, LIVE, DISABLED
  const [editModalGame, setEditModalGame] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const showToast = (msg, isError = false) => {
    setToastMessage({ text: msg, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await GameService.getAllGames();
      const list = Array.isArray(res) ? res : (res.games || res.data || []);
      setGames(list);
    } catch (err) {
      console.error('Error fetching admin games:', err);
      showToast('Failed to load games list', true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (game) => {
    const nextStatus = !game.isEnabled;
    setTogglingId(game.gameId);

    // Optimistic UI update
    setGames((prev) =>
      prev.map((g) =>
        g.gameId === game.gameId
          ? {
              ...g,
              isEnabled: nextStatus,
              badge: nextStatus ? 'LIVE NOW' : 'DISABLED',
              badgeColor: nextStatus ? '#10B981' : '#EF4444',
            }
          : g
      )
    );

    try {
      const res = await GameService.toggleGame(game.gameId, nextStatus);
      showToast(res.message || `Game ${game.title} is now ${nextStatus ? 'ON (LIVE)' : 'OFF (DISABLED)'}`);
      if (res.game) {
        setGames((prev) =>
          prev.map((g) => (g.gameId === game.gameId ? { ...g, ...res.game } : g))
        );
      }
    } catch (err) {
      console.error('Error toggling game:', err);
      showToast(`Failed to toggle ${game.title}`, true);
      // Revert on failure
      setGames((prev) =>
        prev.map((g) =>
          g.gameId === game.gameId
            ? { ...g, isEnabled: game.isEnabled, badge: game.badge, badgeColor: game.badgeColor }
            : g
        )
      );
    } finally {
      setTogglingId(null);
    }
  };

  const openEditModal = (game) => {
    setEditModalGame(game);
    setEditForm({
      title: game.title || '',
      tagline: game.tagline || '',
      description: game.description || '',
      pillText: game.pillText || '',
      rewards: game.rewards || '',
      maintenanceMessage: game.maintenanceMessage || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editModalGame) return;
    setSavingEdit(true);

    try {
      const res = await GameService.updateGame(editModalGame.gameId, editForm);
      showToast(res.message || 'Game details updated successfully!');
      if (res.game) {
        setGames((prev) =>
          prev.map((g) => (g.gameId === editModalGame.gameId ? { ...g, ...res.game } : g))
        );
      }
      setEditModalGame(null);
    } catch (err) {
      console.error('Error saving game:', err);
      showToast('Failed to update game details', true);
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredGames = games.filter((g) => {
    if (filter === 'LIVE') return g.isEnabled === true;
    if (filter === 'DISABLED') return g.isEnabled === false;
    return true;
  });

  const liveCount = games.filter((g) => g.isEnabled).length;
  const disabledCount = games.filter((g) => !g.isEnabled).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold text-white transition-all transform animate-bounce ${
            toastMessage.isError ? 'bg-red-600' : 'bg-emerald-600'
          }`}
        >
          {toastMessage.isError ? <FiAlertCircle className="w-5 h-5" /> : <FiCheckCircle className="w-5 h-5" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 text-purple-700 rounded-xl">
              <FiPlay className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Games Manager & Status Control</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Turn games ON or OFF in real-time. Changes immediately reflect in the mobile app.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={fetchGames}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-semibold shadow-sm transition"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh Status
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Games</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{games.length}</p>
          </div>
          <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center text-slate-600 font-bold text-lg">
            🎮
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Live & Active (ON)</p>
            <p className="text-2xl font-black text-emerald-600 mt-1">{liveCount}</p>
          </div>
          <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Disabled / Coming Soon (OFF)</p>
            <p className="text-2xl font-black text-amber-600 mt-1">{disabledCount}</p>
          </div>
          <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
            <FiPower className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setFilter('ALL')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
            filter === 'ALL'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          All Games ({games.length})
        </button>
        <button
          onClick={() => setFilter('LIVE')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
            filter === 'LIVE'
              ? 'bg-emerald-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Live Only ({liveCount})
        </button>
        <button
          onClick={() => setFilter('DISABLED')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
            filter === 'DISABLED'
              ? 'bg-amber-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Disabled / Off ({disabledCount})
        </button>
      </div>

      {/* Games List Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          <FiRefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-3" />
          <p className="text-sm font-medium">Loading game configurations...</p>
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          <FiInfo className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <p className="text-base font-bold text-slate-700">No games found</p>
          <p className="text-xs text-slate-400 mt-1">Try changing the filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredGames.map((game) => {
            const isLive = game.isEnabled === true;
            const isToggling = togglingId === game.gameId;

            return (
              <div
                key={game.gameId}
                className={`bg-white rounded-2xl border transition-all duration-300 shadow-sm hover:shadow-md overflow-hidden flex flex-col justify-between ${
                  isLive ? 'border-emerald-300 ring-2 ring-emerald-50' : 'border-slate-200 opacity-95'
                }`}
              >
                {/* Top Section */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-100 to-indigo-50 border border-purple-200 flex items-center justify-center text-2xl shadow-inner">
                        {game.icon || '🎮'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-extrabold text-slate-900 text-base">{game.title}</h3>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                              isLive
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {isLive ? 'LIVE NOW' : 'DISABLED'}
                          </span>
                        </div>
                        <p className="text-xs font-semibold text-purple-700 mt-0.5">{game.tagline}</p>
                      </div>
                    </div>

                    {/* Big Intuitive Toggle Switch */}
                    <div className="flex flex-col items-end">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isLive}
                          disabled={isToggling}
                          onChange={() => handleToggle(game)}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-14 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all ${
                            isLive ? '!bg-emerald-500' : 'bg-slate-300'
                          } ${isToggling ? 'opacity-50' : ''}`}
                        ></div>
                      </label>
                      <span className={`text-[11px] font-black mt-1 ${isLive ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {isToggling ? 'Updating...' : isLive ? 'STATUS: ON' : 'STATUS: OFF'}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed mb-4">{game.description}</p>

                  {/* Highlights / Badges */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {game.pillText && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 font-semibold">
                        {game.pillText}
                      </span>
                    )}
                    {game.rewards && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 font-bold">
                        <FiAward className="w-3.5 h-3.5 text-amber-600" />
                        {game.rewards}
                      </span>
                    )}
                  </div>
                </div>

                {/* Bottom Footer Actions */}
                <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">
                    Game Identifier: <code className="bg-slate-200 px-1.5 py-0.5 rounded text-purple-700 font-mono">{game.gameId}</code>
                  </div>

                  <button
                    onClick={() => openEditModal(game)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-xs font-semibold text-slate-700 shadow-2xs transition"
                  >
                    <FiEdit className="w-3.5 h-3.5 text-slate-500" />
                    Edit Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Details Modal */}
      {editModalGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 relative animate-in fade-in zoom-in-95">
            <button
              onClick={() => setEditModalGame(null)}
              className="absolute top-5 right-5 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"
            >
              <FiX className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-xl">
                {editModalGame.icon || '🎮'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Edit {editModalGame.title}</h2>
                <p className="text-xs text-slate-500">Update rewards text, tagline, or maintenance notice</p>
              </div>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Game Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Tagline</label>
                <input
                  type="text"
                  value={editForm.tagline}
                  onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Rewards Description</label>
                <input
                  type="text"
                  value={editForm.rewards}
                  onChange={(e) => setEditForm({ ...editForm, rewards: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. ₹500 - ₹50,000 Cash Drops"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Pill Highlight</label>
                <input
                  type="text"
                  value={editForm.pillText}
                  onChange={(e) => setEditForm({ ...editForm, pillText: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. 📍 Real GPS Radar"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Description</label>
                <textarea
                  rows="3"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Maintenance Notice (If OFF)</label>
                <input
                  type="text"
                  value={editForm.maintenanceMessage}
                  onChange={(e) => setEditForm({ ...editForm, maintenanceMessage: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. This game is currently undergoing maintenance."
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditModalGame(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;

